import { useEffect, useRef, useState } from 'react';
import {
	IAIPromptTemplate,
	findPromptPlaceholder,
	findPromptPlaceholders,
	getAIPromptText,
} from '../../../../models/AIPromptTemplate';

const getPlaceholderProblem = (placeholder: string) => `Replace ${placeholder} with your own words before continuing.`;

export interface IAppliedAIPrompt {
	prompt: IAIPromptTemplate;
	text: string;
	/** Hold an immediate save while this is set. */
	unfilledPlaceholder: string | null;
}

/**
 * The state behind an AIPromptField: the value stays the caller's, while which prompt it started from and that
 * prompt's [placeholders] are kept here. Returned rather than reported, so a caller can read them in the same render
 * that builds its own output.
 */
export function useAIPromptField({
	onChange,
	onPromptApplied,
	value,
}: {
	onChange: (value: string) => void;
	/** A picked prompt skips the textarea's change and blur, so a surface that saves on blur saves from here. */
	onPromptApplied?: (applied: IAppliedAIPrompt) => void;
	value: string;
}) {
	const ref = useRef<HTMLTextAreaElement>(null);
	const [appliedPrompt, setAppliedPrompt] = useState<IAIPromptTemplate | null>(null);
	// Kept apart from appliedPrompt, which a save replaces with the user's own words: brackets they typed must never
	// become blanks they are made to fill.
	const [promptPlaceholders, setPromptPlaceholders] = useState<string[]>([]);
	// Acted on once the value holds the prompt's text: a selection inside it means nothing before then.
	const [pending, setPending] = useState<{
		applied: IAppliedAIPrompt;
		selection: { end: number; start: number };
	} | null>(null);
	const onPromptAppliedRef = useRef(onPromptApplied);
	onPromptAppliedRef.current = onPromptApplied;

	const applyPrompt = (prompt: IAIPromptTemplate) => {
		const text = getAIPromptText(prompt);
		onChange(text);
		setAppliedPrompt(prompt);
		const placeholders = findPromptPlaceholders(text);
		setPromptPlaceholders(placeholders);
		// The first [placeholder] is what the user has to fill in, so it is selected ready to type over. A prompt
		// without one leaves the cursor at the end.
		setPending({
			applied: { prompt, text, unfilledPlaceholder: placeholders[0] ?? null },
			selection: findPromptPlaceholder(text) ?? { end: text.length, start: text.length },
		});
	};

	useEffect(() => {
		if (!pending) {
			return;
		}
		// Before focusing, or a caller that snapshots the value on focus would take the prompt as already saved.
		onPromptAppliedRef.current?.(pending.applied);
		ref.current?.focus();
		ref.current?.setSelectionRange(pending.selection.start, pending.selection.end);
		setPending(null);
	}, [pending]);

	// Left in, a blank reaches the AI as an instruction ("[describe what is new]") and it invents something to fill it.
	const unfilledPlaceholder = promptPlaceholders.find(placeholder => value.includes(placeholder)) ?? null;

	// Derived rather than stored so it clears itself the moment the value stops being the prompt's words, instead of
	// relying on every edit path remembering to reset it.
	const sourcePrompt = appliedPrompt && value.trim() === getAIPromptText(appliedPrompt) ? appliedPrompt : null;

	return {
		applyPrompt,
		onChange,
		/** A save makes the value that prompt's words, so it is credited to it and there is nothing left to save. */
		onSaved: setAppliedPrompt,
		placeholderProblem: unfilledPlaceholder ? getPlaceholderProblem(unfilledPlaceholder) : null,
		ref,
		sourcePrompt,
		unfilledPlaceholder,
		value,
	};
}
